"use client";

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";

interface RenameFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  onRename: (newName: string) => Promise<void>;
}

export function RenameFileDialog({
  isOpen,
  onClose,
  fileName,
  onRename,
}: RenameFileDialogProps) {
  const [newName, setNewName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const [error, setError] = useState("");

  // Extract file name without extension when dialog opens
  useEffect(() => {
    if (isOpen && fileName) {
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
      setNewName(nameWithoutExt);
      setError("");
    }
  }, [isOpen, fileName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName || newName.trim() === "") {
      setError("File name cannot be empty");
      return;
    }

    // Validate file name (no invalid characters)
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newName)) {
      setError('File name cannot contain: < > : " / \\ | ? *');
      return;
    }

    setIsRenaming(true);
    setError("");

    try {
      await onRename(newName.trim());
      onClose();
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to rename file";
      // Check if error is about file already existing
      if (errorMessage.toLowerCase().includes("already exists") || 
          errorMessage.toLowerCase().includes("fileexistserror")) {
        setError("File name already exists");
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const handleClose = () => {
    if (!isRenaming) {
      setNewName("");
      setError("");
      onClose();
    }
  };

  // Get file extension
  const fileExtension = fileName.includes(".") 
    ? fileName.substring(fileName.lastIndexOf("."))
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-black/90 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Rename File</DialogTitle>
          <DialogDescription className="text-white/60">
            Enter a new name for the file. The extension will be preserved.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="file-name" className="text-sm font-medium text-white">
                Current File Name
              </label>
              <div className="text-sm text-white/70 bg-white/5 p-2 rounded border border-white/10">
                {fileName}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="new-name" className="text-sm font-medium text-white">
                New File Name (without extension)
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="new-name"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter new file name"
                  className="bg-black/20 border-white/10 text-white placeholder:text-white/40"
                  disabled={isRenaming}
                  required
                  autoFocus
                />
                {fileExtension && (
                  <span className="text-sm text-white/60 whitespace-nowrap">
                    {fileExtension}
                  </span>
                )}
              </div>
              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}
              <p className="text-xs text-white/50">
                The file will be renamed to: <span className="font-mono">{newName || "..."}{fileExtension}</span>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isRenaming}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isRenaming || !newName.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isRenaming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Renaming...
                </>
              ) : (
                "Rename"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

